import { Component, OnInit, AfterContentInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, ValidatorFn, FormArray, FormControl, Validators } from '@angular/forms';
import { NgxPermissionsService } from 'ngx-permissions';

import deepEqual from 'deep-equal'

import { SmsSettingsModel } from '@models/smsSettings.model';
import { phoneNumberMask, emptyReminderSmsModel } from '../../../core/utilities/data-utils';
import { SmsModel } from '@models/index';
import { daysForRemindersOpt } from '@utilities/data-utils'
import { SearchCountryField, CountryISO } from '@capgo/ngx-intl-tel-input';

@Component({
  selector: 'app-sms-content-form',
  templateUrl: './sms-content-form.component.html',
  styleUrls: ['./sms-content-form.component.sass']
})
export class SmsContentFormComponent implements OnInit, AfterContentInit {

  @Input() smsContent: SmsSettingsModel;
  @Input() landingURL: string;
  @Output() onCancel = new EventEmitter();
  @Output() onSave = new EventEmitter<SmsSettingsModel>();
  @Output() smsTest = new EventEmitter();

  messageSize: number;
  urlSuffixSize: number;
  totalCharacters: number;
  numberSmsNeeded: number;
  phoneForm: FormGroup;

  smsForm: FormGroup;
  SearchCountryField = SearchCountryField;
	CountryISO = CountryISO;
	preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.Canada, CountryISO.Australia, CountryISO.UnitedKingdom];

  undoDisable: boolean = true;
  saveDisable: boolean = true;
  sendTestDisable: boolean = true;

  phoneNumberMask = phoneNumberMask;

  selectedTabIndex: number = 0;
  emptyReminder: SmsModel = emptyReminderSmsModel();
  sms: FormArray;
  numberOfSms: number = 1;
  permissions: any;

  constructor(
    private fb: FormBuilder,
    private permissionsService: NgxPermissionsService
  ) {
  }

  ngOnInit() {
    this.permissions = this.permissionsService.getPermissions();
    this.smsForm = this.fb.group({
      sms: this.fb.array([this.smsContent.reviewRequest, ...this.smsContent.reminders].map(item => this.fb.group(item)), this.smsValidation())
    }
    );
    this.formChanged();
    this.sms = this.smsForm.get('sms') as FormArray;
    this.numberOfSms = this.sms.length;
    this.phoneForm = this.fb.group({
      phoneNumberSms: new FormControl('', [Validators.required])
    }, {
      validator: (formControl) => {
        return (formControl.value.phoneNumberSms && !formControl.controls['phoneNumberSms'].invalid) ?  null : { invalid: true }
      }
    });
  }

  ngAfterContentInit() {
  }

  formChanged(): void {
    this.smsForm.valueChanges.subscribe(
      val => {
        this.allowUndo();
        this.allowSave();
      }
    )
  }

  smsValidation(): ValidatorFn {
    const validator: ValidatorFn = (formArray: FormArray) => {

      let daysForReminder: number[] = [];

      for(let i = 0; i < formArray.length; i++){
        if(i) daysForReminder.push(formArray.controls[i].get('daysForReminder').value)
      }
      return ((daysForReminder.length ? (daysForReminder.filter((days) => days == null ? true : false).length > 0 ? false : true ) : true)
      && (daysForReminder.length ? (daysForReminder.reduce((acc,days,index) =>  index ? acc && (days > daysForReminder[index-1]) : true, true)) ? true : false : true))
      ? null : {invalid: true};
    };

    return validator;
  }

  allowUndo() {
    this.undoDisable = deepEqual({...{}, sms: [this.smsContent.reviewRequest, ...this.smsContent.reminders]}, this.smsForm.value)
  }

  allowSave() {
    this.saveDisable = !this.smsForm.valid || deepEqual({...{}, sms: [this.smsContent.reviewRequest, ...this.smsContent.reminders]}, this.smsForm.value)
  }

  allowSendTest() {
    this.sendTestDisable = !(this.phoneForm.value.phoneNumberSms && !this.phoneForm.controls['phoneNumberSms'].invalid)
  }

  calculateTotalCharacters() {
    this.sms = this.smsForm.get('sms') as FormArray;

    return this.sms.controls[this.selectedTabIndex].get('smsBody').value.length + this.landingURL.length
  }

  calculateSmsNeeded() {
    this.sms = this.smsForm.get('sms') as FormArray;

    return Math.ceil((this.sms.controls[this.selectedTabIndex].get('smsBody').value.length + this.landingURL.length) / 146)
  }

  calculateUrlSuffixSize() {
    return this.landingURL.includes('http')
      ? this.landingURL.length
      : 0
  }

  remainingCharacters() {
    this.sms = this.smsForm.get('sms') as FormArray;
    return 146 - ((this.sms.controls[this.selectedTabIndex].get('smsBody').value.length + this.landingURL.length) % 146)
  }

  calculateMessageSize() {
    this.sms = this.smsForm.get('sms') as FormArray;
    return this.sms.controls[this.selectedTabIndex].get('smsBody').value.length
  }

  undo() {
    this.selectedTabIndex = 0;
    this.sms = this.smsForm.get('sms') as FormArray;
    this.sms.controls.splice(0)
    this.sms.push(this.fb.group(this.smsContent.reviewRequest))
    for (let i = 0; i < this.smsContent.reminders.length; i++) {
      this.sms.push(this.fb.group(this.smsContent.reminders[i]))
    }
  }

  save() {
    this.onSave.emit({
      ...{ }, 
      reviewRequest: this.smsForm.get('sms').value[0],
      reminders: [...this.smsForm.get('sms').value].slice(1,this.smsForm.get('sms').value.length)
    })
  }

  sendTest() {
    this.smsTest.emit({
      ...{ },
      sms: this.smsForm.get('sms').value[this.selectedTabIndex],
      phoneNumber: this.phoneForm.value.phoneNumberSms.internationalNumber
    })
    this.phoneForm.reset()
  }

  onTab(selectedIndex) {
    this.sms = this.smsForm.get('sms') as FormArray;
    this.selectedTabIndex = selectedIndex;
    this.allowSendTest();
  }

  addReminder() {
    this.sms = this.smsForm.get('sms') as FormArray;
    this.sms.push(this.fb.group({
      ...{},
      label: `Reminder ${this.sms.length}`,
      smsBody: this.emptyReminder.smsBody,
      daysForReminder:  this.sms.length < 11 ?  daysForRemindersOpt[this.sms.length - 1] : daysForRemindersOpt[daysForRemindersOpt.length - 1],
      minDaysForReminder: this.sms.length > 1 ? this.sms.controls[this.sms.length - 1].get('daysForReminder').value : this.emptyReminder.daysForReminder
    }
    ))
    this.numberOfSms = this.sms.length;
    this.selectedTabIndex =  this.sms.length - 1;
  }

  deleteReminder() {
    const deletedTab = this.selectedTabIndex;
    this.selectedTabIndex = 0;
    this.sms = this.smsForm.get('sms') as FormArray;
    this.sms.removeAt(deletedTab);

    for (let i = 1; i < this.sms.length; i++) {
      this.sms.controls[i].get('label').setValue(`Reminder ${i}`)
    }

    this.numberOfSms = this.sms.length;
  }

  getLabel(i) {
    return this.getControls()[i].value.label;
  }
  getDaysForReminder(i) {
    return this.getControls()[i].value.daysForReminder;
  }
  getMinDaysForReminder(i) {
    return this.getControls()[i].value.minDaysForReminder;
  }
  getControls() {
    return (<FormArray>this.smsForm.get('sms')).controls;
  }
}
